import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "../../../Hooks/useStore";
import { useCallback } from "react";

export type GeneratedFilesTypes = {
  fileLink: string;
  fileType: string;
};

export type GeneratedVideoTypes = {
  videoLink: string;
  videoType: string;
};

export type GeneratedAudioTypes = {
  audioLink: string;
  audioType: string;
};

export type StreamChatTypes = {
  id: string;
  sessionEditedId?: string;
  message: string;
  res: string;
  resources: { link: string; favicon: string }[] | null;
  sessionId: string;
  image: StreamImageTypes | null;
  sentFiles?: string[];
  generatedFiles?: GeneratedFilesTypes[] | null;
  generatedVideo?: GeneratedVideoTypes[] | null;
  generatedAudio?: GeneratedAudioTypes[] | null;
};

export type StreamImageTypes = { url: string[]; revisedPrompt: string };
export type StatusStreamTypes = {
  status: string;
  label: string;
  delay?: number;
};

export type StreamedChatState = {
  streamedChats: StreamChatTypes[] | null;
  streamingChatUserMessage: string;
  streamingChatRes: string;
  streamingChatId: string;
  preventToGetChats: boolean;
  error: { message: string; retriable: boolean,singleError?:boolean };
  webSearching: boolean;
  resources: { link: string; favicon: string }[] | null;
  nextResource: boolean;
  initLoading: boolean;
  sessionId: string;
  sessionEditedId: string;
  statusStates?: StatusStreamTypes | null;
  image: StreamImageTypes | null;
  needAccountUpgrade?: string;
  sentFiles?: string[];
  generatedFiles: GeneratedFilesTypes[] | null;
  generatedVideo?: GeneratedVideoTypes[] | null;
  generatedAudio?: GeneratedAudioTypes[] | null;
};

const initialState = {
  streamedChats: [],
  streamingChatUserMessage: "",
  streamingChatRes: "",
  streamingChatId: "",
  preventToGetChats: false,
  error: { message: "", retriable: false ,singleError:false },
  webSearching: false,
  resources: null,
  nextResource: false,
  initLoading: false,
  sessionId: "",
  sessionEditedId: "",
  statusStates: null,
  image: null,
  needAccountUpgrade: "",
  sentFiles: [],
  generatedFiles: null,
  generatedVideo: null,
  generatedAudio: null,
} as StreamedChatState;

export const streamChatSlice = createSlice({
  name: "streamChatSlice",
  reducers: {
    setStreamedChat: (state, action: PayloadAction<StreamChatTypes>) => {
      state.streamedChats?.push(action.payload);
    },
    setStreamingChatRes: (state, action: PayloadAction<string>) => {
      state.streamingChatRes += action.payload;
    },
    resetStreamingChatRes: (state) => {
      state.streamingChatRes = "";
    },
    setStreamingUserChatMessage: (state, action: PayloadAction<string>) => {
      state.streamingChatUserMessage = action.payload;
    },
    setStreamingChatId: (state, action: PayloadAction<string>) => {
      state.streamingChatId = action.payload;
    },
    setPreventToGetChats: (state, action) => {
      state.preventToGetChats = action.payload;
    },
    setError: (state, action: PayloadAction<{ errorMessage: string; retry: boolean; singleError?:boolean }>) => {
      state.error.message = action.payload.errorMessage;
      state.error.retriable = action.payload.retry;
      state.error.singleError = action.payload.singleError;
    },
    setWebSearching: (state, action) => {
      state.webSearching = action.payload;
    },
    setResources: (state, action) => {
      state.resources = action.payload;
    },
    setResetResources: (state) => {
      state.resources = null;
    },
    setNextResource: (state, action: PayloadAction<boolean>) => {
      state.nextResource = action.payload;
    },
    setInitLoading: (state, action: PayloadAction<boolean>) => {
      state.initLoading = action.payload;
    },
    setResetAllStreamed: (state) => {
      state.streamedChats = [];
      state.streamingChatUserMessage = "";
      state.streamingChatRes = "";
      state.streamingChatId = "";
      state.preventToGetChats = false;
      state.error = { message: "", retriable: false,singleError:false };
      state.resources = null;
      state.sentFiles = [];
    },
    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },
    setSessionEditedId: (state, action: PayloadAction<string>) => {
      state.sessionEditedId = action.payload;
    },
    setStatusStates: (state, action: PayloadAction<StatusStreamTypes>) => {
      state.statusStates = action.payload;
    },
    setImage: (state, action: PayloadAction<StreamImageTypes | null>) => {
      state.image = action.payload;
    },
    setNeedAccountUpgrade: (state, action: PayloadAction<string>) => {
      state.needAccountUpgrade = action.payload;
    },
    setSentFiles: (state, action: PayloadAction<string[]>) => {
      state.sentFiles = action.payload;
    },
    setResetFiles: (state) => {
      state.sentFiles = [];
    },
    setGeneratedFiles: (state, action: PayloadAction<GeneratedFilesTypes[] | null>) => {
      state.generatedFiles = action.payload;
    },
    setGeneratedVideo: (state, action: PayloadAction<GeneratedVideoTypes[] | null>) => {
      state.generatedVideo = action.payload;
    },
    setGeneratedAudio: (state, action: PayloadAction<GeneratedAudioTypes[] | null>) => {
      state.generatedAudio = action.payload;
    },
    truncateStreamedChatsAfter: (state, action: PayloadAction<number>) => {
      const cutoff = action.payload;
      if (!cutoff || !state.streamedChats?.length) return;

      state.streamedChats = state.streamedChats.filter((chat) => {
        const edited = chat.sessionEditedId;
        const session = chat.sessionId;
        const raw =
          edited && edited !== "null" && edited !== "0" ? edited : session;
        const groupId = Number(raw);
        if (!groupId || Number.isNaN(groupId) || groupId <= 0) return true;
        return groupId <= cutoff;
      });
    },
    cancelStream: () => {},
  },
  initialState,
});

export const {
  setStreamedChat,
  setStreamingChatRes,
  setStreamingUserChatMessage,
  setStreamingChatId,
  resetStreamingChatRes,
  setPreventToGetChats,
  setError,
  setResetAllStreamed,
  setWebSearching,
  setResources,
  setResetResources,
  setInitLoading,
  setSessionId,
  setSessionEditedId,
  setStatusStates,
  setImage,
  setNeedAccountUpgrade,
  setSentFiles,
  setResetFiles,
  setGeneratedFiles,
  setGeneratedVideo,
  setGeneratedAudio,
  truncateStreamedChatsAfter,
} = streamChatSlice.actions || {};

export default streamChatSlice.reducer;

export const useStreamedChat = () => {
  const {
    streamedChats,
    streamingChatId,
    streamingChatRes,
    streamingChatUserMessage,
    preventToGetChats,
    error,
    webSearching,
    resources,
    nextResource,
    initLoading,
    sessionId,
    sessionEditedId,
    statusStates,
    image,
    needAccountUpgrade,
    sentFiles,
    generatedFiles,
    generatedVideo,
    generatedAudio,
  } = useAppSelector((state) => state.streamedChat);
  const dispatch = useAppDispatch();

  const dispatchSetWebSearching = useCallback(
    (data: boolean) => {
      dispatch(setWebSearching(data));
    },
    [dispatch],
  );

  const dispatchSetNeedAccountUpgrade = useCallback(
    (data: string) => {
      dispatch(setNeedAccountUpgrade(data));
    },
    [dispatch],
  );

  const dispatchResetStreamedChats = useCallback(() => {
    dispatch(setResetAllStreamed());
  }, [dispatch]);

  return {
    streamedChats,
    streamingChatId,
    streamingChatRes,
    streamingChatUserMessage,
    preventToGetChats,
    error,
    webSearching,
    resources,
    nextResource,
    statusStates,
    needAccountUpgrade,
    image,
    dispatchSetWebSearching,
    dispatchResetStreamedChats,
    initLoading,
    sessionId,
    sessionEditedId,
    dispatchSetNeedAccountUpgrade,
    sentFiles,
    generatedFiles,
    generatedVideo,
    generatedAudio,
  };
};
